import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Alert, List, Modal, Progress, Table, Tabs, Typography } from 'antd';
import React from 'react';
import {
  DownloadProgress,
  VariantSummary,
} from '../../utils/designDownloadService';

const { Text } = Typography;

interface DownloadProgressModalProps {
  visible: boolean;
  onCancel: () => void;
  progress: DownloadProgress[];
  estimatedSize?: string;
  zipFileName?: string;
  variantSummary?: VariantSummary[];
}

const DownloadProgressModal: React.FC<DownloadProgressModalProps> = ({
  visible,
  onCancel,
  progress,
  estimatedSize,
  zipFileName,
  variantSummary,
}) => {
  const totalFiles = progress.length > 0 ? progress[0]?.total || 0 : 0;
  const completedFiles = progress.filter((p) => p.status === 'complete').length;
  const errorFiles = progress.filter((p) => p.status === 'error').length;
  const isProcessing = progress.some((p) => p.status === 'processing');
  const isComplete =
    progress.length > 0 &&
    progress.every((p) => p.status === 'complete' || p.status === 'error');

  const overallPercent =
    totalFiles > 0 ? Math.round((completedFiles / totalFiles) * 100) : 0;

  const getStatusIcon = (status: DownloadProgress['status']) => {
    switch (status) {
      case 'complete':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'downloading':
      case 'processing':
        return <LoadingOutlined style={{ color: '#1890ff' }} />;
      default:
        return null;
    }
  };

  const getStatusText = (status: DownloadProgress['status']) => {
    switch (status) {
      case 'complete':
        return 'Hoàn thành';
      case 'error':
        return 'Thất bại';
      case 'downloading':
        return 'Đang tải...';
      case 'processing':
        return 'Đang xử lý...';
      default:
        return 'Đang chờ...';
    }
  };

  const getProgressStatus = () => {
    if (errorFiles > 0 && completedFiles === 0) return 'exception';
    if (errorFiles > 0) return 'active';
    if (isComplete) return 'success';
    return 'active';
  };

  // Variant summary table columns
  const variantColumns = [
    {
      title: 'Màu sắc',
      dataIndex: 'color',
      key: 'color',
      render: (color: string) => (
        <Text strong style={{ textTransform: 'capitalize' }}>
          {color}
        </Text>
      ),
    },
    {
      title: 'Kích thước',
      dataIndex: 'size',
      key: 'size',
      render: (size: string) => (
        <Text style={{ fontWeight: 'bold', fontSize: '14px' }}>{size}</Text>
      ),
    },
    {
      title: 'Sản phẩm',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: number) => (
        <Text strong style={{ color: '#1890ff' }}>
          {quantity}
        </Text>
      ),
    },
    {
      title: 'Thiết kế',
      dataIndex: 'designs',
      key: 'designs',
      render: (designs: number) => (
        <Text type="secondary">{designs} thiết kế</Text>
      ),
    },
  ];

  // Progress tab content
  const progressTabContent = (
    <>
      {errorFiles > 0 && (
        <Alert
          message={`${errorFiles} tệp không thể tải xuống`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {isProcessing && (
        <Alert
          message="Đang tạo tệp ZIP..."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {isComplete && (
        <Alert
          message={`Tải xuống hoàn tất! Đã lưu tệp ${
            zipFileName || 'designs.zip'
          }.`}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ maxHeight: 300, overflow: 'auto' }}>
        <List
          size="small"
          dataSource={progress}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <List.Item.Meta
                avatar={getStatusIcon(item.status)}
                title={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Text ellipsis style={{ maxWidth: '300px' }}>
                      {item.fileName}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {getStatusText(item.status)}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </div>
    </>
  );

  // Variant summary tab content
  const variantTabContent =
    variantSummary && variantSummary.length > 0 ? (
      <div style={{ maxHeight: 400, overflow: 'auto' }}>
        <Table
          dataSource={variantSummary}
          columns={variantColumns}
          pagination={false}
          size="small"
          rowKey={(record) =>
            `${record.color}-${record.size}-${record.productName}`
          }
          summary={() => {
            const totalQuantity = variantSummary.reduce(
              (sum, item) => sum + item.quantity,
              0
            );
            const totalDesigns = variantSummary.reduce(
              (sum, item) => sum + item.designs,
              0
            );
            return (
              <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>Tổng cộng</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <Text strong style={{ color: '#1890ff' }}>
                    {totalQuantity}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <Text strong type="secondary">
                    {totalDesigns} thiết kế
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </div>
    ) : (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Text type="secondary">Không có thông tin biến thể</Text>
      </div>
    );

  return (
    <Modal
      title="Tải xuống thiết kế"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      closable={isComplete}
      maskClosable={false}
    >
      {/* Overall Progress Section */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            marginBottom: 8,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Text strong>Tổng tiến độ</Text>
          <Text type="secondary">
            {completedFiles} / {totalFiles} tệp
            {estimatedSize && ` (${estimatedSize})`}
          </Text>
        </div>
        <Progress
          percent={overallPercent}
          status={getProgressStatus()}
          strokeColor={errorFiles > 0 ? '#ff4d4f' : undefined}
        />
      </div>

      {/* Tabs Section */}
      <Tabs
        defaultActiveKey="progress"
        items={[
          {
            key: 'progress',
            label: 'Tiến độ tải xuống',
            children: progressTabContent,
          },
          {
            key: 'variants',
            label: `Thống kê biến thể${
              variantSummary ? ` (${variantSummary.length})` : ''
            }`,
            children: variantTabContent,
          },
        ]}
      />

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {isComplete
            ? 'Bạn có thể đóng hộp thoại này'
            : 'Vui lòng giữ cửa sổ này mở trong khi tải xuống...'}
        </Text>
      </div>
    </Modal>
  );
};

export default DownloadProgressModal;
